/**
 * Passkey registration page — opened in a Chrome Custom Tab on native.
 *
 * Chrome handles WebAuthn natively with full biometric support, unlike the
 * Android Credential Manager API which has additional restrictions for
 * non-browser apps (domain recoverability checks, provider discovery issues).
 *
 * Flow:
 *  1. App opens this page in Chrome Custom Tab with access token in URL hash
 *  2. Page calls Cognito StartWebAuthnRegistration
 *  3. Chrome's navigator.credentials.create() triggers biometric prompt
 *  4. Page calls Cognito CompleteWebAuthnRegistration
 *  5. Redirects back to app via deep link
 */

import { LitElement, html, css } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { logger } from '../../utils/logger.js'
import { loadAuthConfig, authConfig } from '../../services/auth/auth-config.js'

const log = logger.create('register-passkey')

/** Decode a base64url string to an ArrayBuffer. */
function base64urlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

/** Encode an ArrayBuffer to a base64url string. */
function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** Call a Cognito User Pools API action. */
async function cognitoCall(
  action: string,
  payload: Record<string, unknown>
): Promise<Record<string, unknown>> {
  const endpoint = `https://cognito-idp.${authConfig.region}.amazonaws.com`
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${action}`,
    },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    const body = await response.text()
    throw new Error(`Cognito ${action} failed: ${response.status} ${body}`)
  }

  return response.json()
}

@customElement('auth-register-passkey')
export class AuthRegisterPasskey extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100vh;
      background: var(--cairn-background-color, #f8fafc);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }

    .container {
      text-align: center;
      padding: 32px;
      max-width: 400px;
    }

    .spinner {
      width: 48px;
      height: 48px;
      border: 4px solid #e2e8f0;
      border-top-color: #3b82f6;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
      margin: 0 auto 24px;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    .status {
      font-size: 16px;
      color: #64748b;
      margin-bottom: 8px;
    }

    .error {
      color: #dc2626;
      margin-bottom: 16px;
    }

    .success {
      color: #16a34a;
      font-size: 18px;
      margin-bottom: 16px;
    }

    .icon {
      font-size: 48px;
      margin-bottom: 16px;
    }

    .success-icon {
      color: #16a34a;
    }

    .error-icon {
      color: #dc2626;
    }

    button {
      background: #3b82f6;
      color: white;
      border: none;
      padding: 12px 24px;
      border-radius: 8px;
      cursor: pointer;
      font-size: 14px;
    }

    button:hover {
      background: #2563eb;
    }
  `

  @state()
  private status: 'loading' | 'registering' | 'success' | 'error' = 'loading'

  @state()
  private errorMessage = ''

  override connectedCallback(): void {
    super.connectedCallback()
    void this.startRegistration()
  }

  private async startRegistration(): Promise<void> {
    try {
      // Read access token from URL hash
      const hash = window.location.hash.slice(1)
      const params = new URLSearchParams(hash)
      const accessToken = params.get('token')

      if (!accessToken) {
        this.status = 'error'
        this.errorMessage = 'No access token provided.'
        return
      }

      // Load auth config (needed for Cognito region)
      await loadAuthConfig()

      this.status = 'registering'

      // 1. Get creation options from Cognito
      log.info('Starting WebAuthn registration...')
      const data = await cognitoCall('StartWebAuthnRegistration', {
        AccessToken: accessToken,
      })

      const options = data.CredentialCreationOptions as Record<string, unknown>

      // NOTE: Google Password Manager requires domain registration before it
      // will offer the platform authenticator (biometric) for passkey creation.
      // Until taselith.com is registered via https://goo.gle/passkey-gpm-form,
      // only cross-device/security key options will appear on Android.
      // iCloud Keychain (iOS/Safari) works without registration.

      // 2. Convert to browser WebAuthn format and create credential
      const user = options.user as Record<string, string>
      const publicKey: PublicKeyCredentialCreationOptions = {
        ...(options as unknown as PublicKeyCredentialCreationOptions),
        challenge: base64urlToBuffer(options.challenge as string),
        user: {
          ...user,
          id: base64urlToBuffer(user.id),
        } as PublicKeyCredentialUserEntity,
        excludeCredentials: (
          (options.excludeCredentials ?? []) as Array<{
            id: string
            type: PublicKeyCredentialType
            transports?: AuthenticatorTransport[]
          }>
        ).map(cred => {
          const desc: PublicKeyCredentialDescriptor = {
            type: cred.type,
            id: base64urlToBuffer(cred.id),
          }
          if (cred.transports) desc.transports = cred.transports
          return desc
        }),
      }

      const credential = (await navigator.credentials.create({
        publicKey,
      })) as PublicKeyCredential | null

      if (!credential) {
        throw new Error('Passkey creation was cancelled.')
      }

      // 3. Convert response to JSON for Cognito
      const response = credential.response as AuthenticatorAttestationResponse
      const credentialData = {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64url(response.clientDataJSON),
          attestationObject: bufferToBase64url(response.attestationObject),
          transports:
            (response as unknown as { getTransports?: () => string[] }).getTransports?.() ?? [],
          authenticatorData: (response as unknown as { getAuthenticatorData?: () => ArrayBuffer })
            .getAuthenticatorData
            ? bufferToBase64url(
                (
                  response as unknown as { getAuthenticatorData: () => ArrayBuffer }
                ).getAuthenticatorData()
              )
            : undefined,
          publicKey: (
            response as unknown as { getPublicKey?: () => ArrayBuffer | null }
          ).getPublicKey?.()
            ? bufferToBase64url(
                (response as unknown as { getPublicKey: () => ArrayBuffer }).getPublicKey()
              )
            : undefined,
          publicKeyAlgorithm: (
            response as unknown as { getPublicKeyAlgorithm?: () => number }
          ).getPublicKeyAlgorithm?.(),
        },
        authenticatorAttachment:
          (credential as unknown as { authenticatorAttachment?: string }).authenticatorAttachment ??
          undefined,
        clientExtensionResults: credential.getClientExtensionResults(),
      }

      // 4. Complete registration with Cognito
      log.info('Completing registration with Cognito...')
      await cognitoCall('CompleteWebAuthnRegistration', {
        AccessToken: accessToken,
        Credential: credentialData,
      })

      this.status = 'success'

      // Redirect back to app after brief delay
      setTimeout(() => {
        window.location.href = 'com.taselith.app://auth/passkey-complete?success=true'
      }, 1500)
    } catch (err) {
      log.error('Passkey registration failed:', err)
      this.status = 'error'
      this.errorMessage = err instanceof Error ? err.message : String(err)
    }
  }

  private handleClose(): void {
    window.location.href = 'com.taselith.app://auth/passkey-complete?success=false'
  }

  override render(): unknown {
    switch (this.status) {
      case 'loading':
      case 'registering':
        return html`
          <div class="container">
            <div class="spinner"></div>
            <p class="status">
              ${this.status === 'loading' ? 'Preparing...' : 'Touch your fingerprint sensor...'}
            </p>
          </div>
        `
      case 'success':
        return html`
          <div class="container">
            <div class="icon success-icon">&#10003;</div>
            <p class="success">Passkey registered successfully!</p>
            <p class="status">Returning to app...</p>
          </div>
        `
      case 'error':
        return html`
          <div class="container">
            <div class="icon error-icon">&#10007;</div>
            <p class="error">${this.errorMessage}</p>
            <button @click=${this.handleClose}>Return to app</button>
          </div>
        `
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'auth-register-passkey': AuthRegisterPasskey
  }
}
